// src/components/personal/sections/ClientsSection/hooks/useClientSearch.ts
import { useState, useCallback, useMemo, useRef } from 'react';
import { Client } from '../types/types';

interface UseClientSearchProps {
  clients: Client[];
  debounceMs?: number;
  onSearchChange?: (query: string) => void;
}

interface UseClientSearchReturn {
  // Состояние поиска
  searchQuery: string;
  searchResults: Client[];
  isSearching: boolean;
  
  // Методы управления поиском
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
}

export const useClientSearch = ({
  clients,
  onSearchChange
}: UseClientSearchProps): UseClientSearchReturn => {
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Состояние поиска
  const [searchQuery, setSearchQueryState] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  
  // Реф для отслеживания текущего запроса
  const currentQueryRef = useRef<string>('');
  
  // Функция поиска клиентов (перенесена сюда, не меняем)
  const searchClients = useCallback((query: string, clientsList: Client[]): Client[] => {
    if (!query || query.trim() === '') {
      return clientsList;
    }
    
    const cleanedQuery = query.toLowerCase().trim();
    
    // Определяем, является ли запрос числовым
    const isNumericQuery = /^\d+$/.test(cleanedQuery);
    
    // Очищаем поисковый запрос от форматирования для сравнения с телефоном
    const cleanedPhoneQuery = cleanedQuery.replace(/[-\s()+]/g, '');
    
    return clientsList.filter(client => {
      // Для числовых запросов проверяем точное совпадение ID
      if (isNumericQuery) {
        // Строгое совпадение с ID
        if (client.id === cleanedQuery) {
          return true;
        }
        
        // Для номера телефона проводим более строгую проверку
        if (client.phone) {
          const cleanedPhone = client.phone.replace(/[-\s()+]/g, '');
          
          // Проверка на точное начало номера телефона
          if (cleanedPhone.startsWith(cleanedPhoneQuery)) {
            return true;
          }
          
          // Если запрос длиннее 5 символов, проверяем его как полный номер
          // Это поможет найти номер, если он введен полностью или почти полностью
          if (cleanedPhoneQuery.length > 5 && cleanedPhone.includes(cleanedPhoneQuery)) {
            return true;
          }
        }
        
        // Не находим совпадений
        return false;
      } 
      // Для нечисловых запросов (если они будут добавлены в будущем)
      else {
        // Здесь можно добавить поиск по другим полям, например по имени
         if (client.name && client.name.toLowerCase().includes(cleanedQuery)) {
           return true;
         }
        
        return false;
      }
    });
  }, []);
  
  // Обработчик изменения поискового запроса - СУЩЕСТВЕННО УПРОЩЕН
  const setSearchQuery = useCallback((query: string) => {
    // Записываем текущий запрос в реф для отслеживания
    currentQueryRef.current = query;
    
    if (DEBUG) console.log(`useClientSearch: установка запроса "${query}"`);
    
    // Логика для определения, нужно ли показывать индикатор поиска
    const shouldShowSearching = query.trim() !== '';
    
    if (shouldShowSearching) {
      setIsSearching(true);
    }
    
    // Устанавливаем запрос в состояние
    setSearchQueryState(query);
    
    // Вызываем колбэк, если он есть
    if (onSearchChange) {
      onSearchChange(query);
    }
    
    // Сбрасываем индикатор загрузки через небольшое время
    if (shouldShowSearching) {
      setTimeout(() => {
        // Проверяем, что запрос не изменился за время таймаута
        if (currentQueryRef.current === query) {
          setIsSearching(false);
        }
      }, 300);
    } else {
      setIsSearching(false);
    }
  }, [onSearchChange, DEBUG]);
  
  // Очистка поиска
  const clearSearch = useCallback(() => {
    if (DEBUG) console.log('useClientSearch: очистка поиска');
    
    // Сбрасываем состояние
    setSearchQueryState('');
    currentQueryRef.current = '';
    setIsSearching(false);
    
    // Вызываем колбэк, если он есть
    if (onSearchChange) {
      onSearchChange('');
    }
  }, [onSearchChange, DEBUG]);
  
  // Результаты поиска
  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      return clients;
    }
    
    if (DEBUG) console.log(`Поиск по запросу "${searchQuery}" среди ${clients.length} клиентов`);
    
    const results = searchClients(searchQuery, clients);
    
    if (DEBUG) console.log(`Найдено ${results.length} клиентов по запросу "${searchQuery}"`);
    
    return results;
  }, [clients, searchQuery, searchClients, DEBUG]);
  
  return {
    searchQuery,
    searchResults,
    isSearching,
    setSearchQuery,
    clearSearch
  };
};